/**
 * Integration Connection Block Component
 *
 * Renders when the AI includes a toolkit slug reference in its markdown response
 * Shows a connection card for the specified integration
 */

import { FC, useMemo, useEffect, useState } from 'react'
import { ExternalLink } from 'lucide-react'
import { ExtensionConnectionCard } from '@/components/extensions/extension-connection-card'
import { Button } from '@/components/ui/button'

interface ExtensionConnectionBlockProps {
  url: string
  toolkitSlug?: string
}

interface ToolkitInfo {
  id: string
  name: string
  description: string
  toolkitSlug: string
  logo?: string
}

export const ExtensionConnectionBlock: FC<ExtensionConnectionBlockProps> = ({
  url,
  toolkitSlug
}) => {
  const [toolkitInfo, setToolkitInfo] = useState<ToolkitInfo | null>(null)
  const [loading, setLoading] = useState(true)

  // Extract toolkit slug from URL patterns
  const extractedToolkitSlug = useMemo(() => {
    if (toolkitSlug) return toolkitSlug

    // Match patterns like /extensions/connect/gmail or connect:gmail or toolkit:gmail
    const patterns = [
      /\/extensions\/connect\/([a-zA-Z0-9]+)/,
      /connect:([a-zA-Z0-9]+)/,
      /toolkit:([a-zA-Z0-9]+)/,
      /connect-([a-zA-Z0-9]+)/
    ]

    for (const pattern of patterns) {
      const match = url.match(pattern)
      if (match) return match[1].toLowerCase()
    }

    // Hosted Connect Link URLs (legacy initiate + modern link / accounts.composio)
    const isComposioHostedAuth =
      url.includes('backend.composio.dev') ||
      url.includes('accounts.composio.dev') ||
      url.includes('connect.composio.dev') ||
      url.includes('connected_accounts/link') ||
      url.includes('/api/v3/connected_accounts')

    if (isComposioHostedAuth) {
      try {
        const urlObj = new URL(url)
        const searchParams = urlObj.searchParams
        const toolkit =
          searchParams.get('toolkit') ||
          searchParams.get('app') ||
          searchParams.get('toolkit_slug') ||
          searchParams.get('appName')
        if (toolkit) return toolkit.toLowerCase()
      } catch {
        // Malformed URL — fall through to generic connect card.
      }
      return null
    }

    return null
  }, [url, toolkitSlug])

  // Fetch integration details from the connection service
  useEffect(() => {
    if (!extractedToolkitSlug) {
      setLoading(false)
      return
    }

    const fetchToolkitInfo = async () => {
      try {
        // Use your existing composio service endpoint
        const response = await fetch(`/api/extensions/toolkit-details?slug=${extractedToolkitSlug}`)

        if (response.ok) {
          const data = await response.json()

          // Map to our expected format
          const info: ToolkitInfo = {
            id: normalizeToolkitSlug(extractedToolkitSlug),
            name: data.name || getDisplayNameFromSlug(extractedToolkitSlug),
            description: data.description || getDescriptionFromSlug(extractedToolkitSlug),
            toolkitSlug: extractedToolkitSlug,
            logo: data.logo // Provider logo URL
          }

          setToolkitInfo(info)
        } else {
          // Fallback to hardcoded info
          setToolkitInfo({
            id: normalizeToolkitSlug(extractedToolkitSlug),
            name: getDisplayNameFromSlug(extractedToolkitSlug),
            description: getDescriptionFromSlug(extractedToolkitSlug),
            toolkitSlug: extractedToolkitSlug,
            logo: `https://logos.composio.dev/api/${extractedToolkitSlug}`
          })
        }
      } catch (error) {
        console.error('Failed to fetch toolkit info:', error)
        // Fallback to hardcoded info
        setToolkitInfo({
          id: getExtensionIdFromSlug(extractedToolkitSlug),
          name: getDisplayNameFromSlug(extractedToolkitSlug),
          description: getDescriptionFromSlug(extractedToolkitSlug),
          toolkitSlug: extractedToolkitSlug,
          logo: `https://logos.composio.dev/api/${extractedToolkitSlug}`
        })
      } finally {
        setLoading(false)
      }
    }

    fetchToolkitInfo()
  }, [extractedToolkitSlug])

  if (loading) {
    return (
      <div className="my-4 animate-pulse rounded-lg border p-4">
        <div className="flex items-center gap-3">
          <div className="size-12 rounded-lg bg-gray-200"></div>
          <div className="flex-1">
            <div className="mb-2 h-4 w-1/3 rounded bg-gray-200"></div>
            <div className="h-3 w-2/3 rounded bg-gray-200"></div>
          </div>
        </div>
      </div>
    )
  }

  if (!toolkitInfo) {
    // For direct auth URLs, show a generic connection button
    const isComposioHostedAuthFallback =
      url.includes('backend.composio.dev') ||
      url.includes('accounts.composio.dev') ||
      url.includes('connect.composio.dev') ||
      url.includes('connected_accounts/link') ||
      url.includes('/api/v3/connected_accounts')

    if (isComposioHostedAuthFallback) {
      return (
        <div className="my-4">
          <div className="mx-auto w-full max-w-md rounded-lg border bg-blue-50/50 p-4 dark:border-blue-900/40 dark:bg-blue-950/30">
            <div className="flex items-center gap-3">
              <div className="flex size-12 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/50">
                <ExternalLink className="size-6 text-blue-600 dark:text-blue-300" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900 dark:text-blue-100">
                  Connect Your Account
                </h3>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Finish signing in so Losi can use this account for you
                </p>
              </div>
              <Button
                onClick={() => window.open(url, '_blank', 'width=600,height=700')}
                className="bg-blue-600 text-white hover:bg-blue-700"
              >
                <ExternalLink className="mr-2 size-4" />
                Connect
              </Button>
            </div>
          </div>
        </div>
      )
    }

    // Fallback to regular link if we can't parse the integration
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:underline"
      >
        {url}
      </a>
    )
  }

  return (
    <div className="my-4">
      <ExtensionConnectionCard
        extensionId={toolkitInfo.id}
        extensionName={toolkitInfo.name}
        description={toolkitInfo.description}
        icon={toolkitInfo.toolkitSlug}
        toolkitSlug={toolkitInfo.toolkitSlug}
        officialLogo={toolkitInfo.logo}
      />
    </div>
  )
}

// Helper function to normalize integration toolkit slug
function normalizeToolkitSlug(slug: string): string {
  // Convert to lowercase and handle common variations
  const normalizedSlug = slug.toLowerCase().trim()

  // Handle common slug variations
  const slugVariations: Record<string, string> = {
    x: 'twitter',
    google_calendar: 'googlecalendar',
    google_sheets: 'googlesheets',
    google_drive: 'googledrive',
    google_docs: 'googledocs',
    google_tasks: 'googletasks',
    google_photos: 'googlephotos',
    google_meet: 'googlemeet',
    microsoft_teams: 'microsoftteams',
    one_drive: 'onedrive'
  }

  const finalSlug = slugVariations[normalizedSlug] || normalizedSlug
  return finalSlug
}

function getDisplayNameFromSlug(slug: string): string {
  const slugToNameMap: Record<string, string> = {
    gmail: 'Gmail',
    github: 'GitHub',
    googlecalendar: 'Google Calendar',
    googlesheets: 'Google Sheets',
    twitter: 'X (Twitter)',
    notion: 'Notion',
    googledrive: 'Google Drive',
    slack: 'Slack',
    outlook: 'Outlook',
    hubspot: 'HubSpot',
    googledocs: 'Google Docs',
    jira: 'Jira',
    youtube: 'YouTube',
    asana: 'Asana',
    googletasks: 'Google Tasks',
    clickup: 'ClickUp',
    reddit: 'Reddit',
    linkedin: 'LinkedIn',
    microsoftteams: 'Microsoft Teams',
    wrike: 'Wrike',
    calendly: 'Calendly',
    googlephotos: 'Google Photos',
    zoom: 'Zoom',
    googlemeet: 'Google Meet',
    onedrive: 'OneDrive',
    dropbox: 'Dropbox'
  }

  return slugToNameMap[slug] || slug.charAt(0).toUpperCase() + slug.slice(1)
}

function getDescriptionFromSlug(slug: string): string {
  const descriptions: Record<string, string> = {
    gmail: 'Connect to your Gmail account to read, send, and manage emails',
    github: 'Connect to GitHub to manage repositories, issues, and pull requests',
    googlecalendar: 'Connect to Google Calendar to view and manage your calendar events',
    googlesheets: 'Connect to Google Sheets to read and modify spreadsheets',
    twitter: 'Connect to X (formerly Twitter) to read and post tweets',
    notion: 'Connect to Notion to read and modify pages and databases',
    googledrive: 'Connect to Google Drive to manage files and folders',
    slack: 'Connect to Slack to send and read messages',
    outlook: 'Connect to Outlook to read, send, and manage emails',
    hubspot: 'Connect to HubSpot to manage contacts, deals, and marketing',
    googledocs: 'Connect to Google Docs to read and modify documents',
    jira: 'Connect to Jira to manage projects, issues, and sprints',
    youtube: 'Connect to YouTube to view and manage videos and channels',
    asana: 'Connect to Asana to manage tasks and projects',
    googletasks: 'Connect to Google Tasks to manage your to-do lists',
    clickup: 'Connect to ClickUp to manage tasks, docs, and goals',
    reddit: 'Connect to Reddit to read and post content',
    linkedin: 'Connect to LinkedIn to view and manage your professional network',
    microsoftteams: 'Connect to Microsoft Teams to send and read messages',
    wrike: 'Connect to Wrike to manage projects and tasks',
    calendly: 'Connect to Calendly to manage your scheduling',
    googlephotos: 'Connect to Google Photos to view and manage your photos',
    zoom: 'Connect to Zoom to manage meetings and webinars',
    googlemeet: 'Connect to Google Meet to manage meetings',
    onedrive: 'Connect to OneDrive to manage files and folders',
    dropbox: 'Connect to Dropbox to manage files and folders'
  }

  return descriptions[slug] || `Connect to ${getDisplayNameFromSlug(slug)} to enhance your workflow`
}
